import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Club Leaderboard — ranks every user by current WHS Handicap Index.
 *
 * The point of this page is competitive social proof: match-play organisers
 * can see who's genuinely in form (auto-updated index) versus who's still
 * playing off a manually-entered number. Highlights the caller's own row
 * even when they're outside the top 100.
 */
export default function Leaderboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [ownRow, setOwnRow] = useState(null);
  const [totalRanked, setTotalRanked] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "auto"

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get("/leaderboard/handicap");
        if (cancelled) return;
        setRows(data.rows || []);
        setOwnRow(data.own_row || null);
        setTotalRanked(data.total_ranked || 0);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Couldn't load the leaderboard right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRows = useMemo(() => {
    if (filter === "auto") return rows.filter((r) => r.auto_updated);
    return rows;
  }, [rows, filter]);

  return (
    <div className="pb-24 max-w-3xl mx-auto" data-testid="leaderboard-page">
      <div className="px-5 pt-6">
        <div className="flex items-baseline justify-between mb-2">
          <h1 className="font-heading font-black text-3xl tracking-tighter-2 text-[var(--primary)]" data-testid="leaderboard-title">
            Club Board
          </h1>
          <Link to="/app/profile" className="text-xs text-[var(--text-secondary)] hover:underline" data-testid="leaderboard-profile-link">
            Your card →
          </Link>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-4">
          Every Fairway player ranked by current WHS Handicap Index. Auto-updated indices come from actual rounds; manual ones show without the badge.
        </p>

        <div className="flex gap-2 mb-4" role="tablist" data-testid="leaderboard-filter-tabs">
          {[
            { key: "all", label: `Everyone · ${totalRanked}` },
            { key: "auto", label: `Auto WHS only` },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={filter === t.key}
              onClick={() => setFilter(t.key)}
              data-testid={`leaderboard-filter-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                filter === t.key
                  ? "bg-[var(--primary)] text-white"
                  : "bg-white/5 text-[var(--text-secondary)] hover:bg-white/10"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="py-8 text-center text-sm text-[var(--text-secondary)] italic" data-testid="leaderboard-loading">
            Loading rankings…
          </div>
        )}
        {error && (
          <div className="py-3 text-center text-sm text-[var(--accent-terracotta)]" data-testid="leaderboard-error">
            {error}
          </div>
        )}

        {!loading && !error && filteredRows.length === 0 && (
          <div className="py-8 text-center text-sm text-[var(--text-secondary)] italic" data-testid="leaderboard-empty">
            No ranked players yet. Play three complete rounds to appear here.
          </div>
        )}

        {!loading && filteredRows.length > 0 && (
          <div className="space-y-1.5" data-testid="leaderboard-rows">
            {filteredRows.map((r) => (
              <LeaderRow key={r.user_id} row={r} isMe={r.user_id === user?.id} />
            ))}
          </div>
        )}

        {ownRow && !loading && !filteredRows.some((r) => r.user_id === ownRow.user_id) && (
          <div className="mt-6 pt-4 border-t border-white/10" data-testid="leaderboard-own-row-wrapper">
            <p className="text-[10px] uppercase tracking-[3px] font-bold text-[var(--text-secondary)] mb-2">
              Your standing
            </p>
            <LeaderRow row={ownRow} isMe />
          </div>
        )}
      </div>
    </div>
  );
}

function LeaderRow({ row, isMe }) {
  const rankBadge = row.rank <= 3
    ? ["🥇", "🥈", "🥉"][row.rank - 1]
    : `#${row.rank}`;
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
        isMe ? "bg-[var(--primary)]/15 border border-[var(--primary)]/30" : "bg-white/[0.03] hover:bg-white/[0.06]"
      }`}
      data-testid={`leaderboard-row-${row.user_id}`}
    >
      <div className="w-10 text-center font-mono-num font-black text-sm text-[var(--primary)]" data-testid={`leaderboard-rank-${row.user_id}`}>
        {rankBadge}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-1.5 flex-wrap">
          <span className="font-heading font-bold text-sm truncate">
            {row.name}
            {isMe && <span className="ml-1 text-[var(--primary)] text-[10px] uppercase font-black tracking-wider">· You</span>}
          </span>
          {row.auto_updated && (
            <span
              className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 text-[9px] uppercase tracking-wider font-bold"
              title="Automatically updated from real rounds"
              data-testid={`leaderboard-auto-badge-${row.user_id}`}
            >
              Auto WHS
            </span>
          )}
        </div>
        <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">
          {row.eligible_rounds > 0
            ? `${row.eligible_rounds} eligible round${row.eligible_rounds === 1 ? "" : "s"}`
            : "No eligible rounds yet"}
          {row.last_differential != null && (
            <span> · last diff <span className="font-mono-num font-bold">{Number(row.last_differential).toFixed(1)}</span></span>
          )}
        </div>
      </div>
      <div className="text-right" data-testid={`leaderboard-index-${row.user_id}`}>
        <div className="font-mono-num font-black text-lg text-[var(--primary)] leading-none">
          {row.handicap_index != null ? Number(row.handicap_index).toFixed(1) : "—"}
        </div>
        <div className="text-[9px] uppercase font-bold tracking-wider text-[var(--text-secondary)] mt-0.5">
          Index
        </div>
      </div>
    </div>
  );
}
